import { capAll } from "@/lib/strapi/formatters";
import { Box, Button, ScrollArea, Select } from "@mantine/core";
import clsx from "clsx";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import classes from "./TabsComponent.module.css";
import TabScrollIcon from "./TabScrollIcon";

export default function TabsComponent({
  type,
  tabs,
  activeTabValue,
  subjectColorIndex,
  className,
}: {
  className?: string;
  subjectColorIndex: string;
  type: "resource" | "theme" | "subTopic";
  tabs: {
    label: string;
    value: string;
  }[];
  activeTabValue: string;
}) {
  const router = useRouter();
  const scrollRef = useRef<HTMLDivElement>(null);
  const cssVars = {
    "--tc-c6": `var(--mantine-color-${subjectColorIndex}-6)`,
    "--tc-c7": `var(--mantine-color-${subjectColorIndex}-7)`,
    "--tc-c8": `var(--mantine-color-${subjectColorIndex}-8)`,
    "--tc-c9": `var(--mantine-color-${subjectColorIndex}-9)`,
  } as React.CSSProperties;
  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollAreaElement = scrollRef.current;
        const activeButton: HTMLButtonElement | undefined =
          scrollAreaElement?.querySelector(`.resource-tabs .activeTab`) ??
          undefined;
        if (scrollAreaElement && activeButton) {
          const buttonCenter =
            activeButton.offsetLeft + activeButton.clientWidth / 2;
          const viewportCenter = scrollAreaElement.clientWidth / 2;

          scrollAreaElement.scrollTo({
            left: Math.max(0, buttonCenter - viewportCenter),
            behavior: "smooth",
          });
        }
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [activeTabValue]);

  return (
    <>
      <div className={classes.desktopOnly} style={cssVars}>
        <Box
          className={classes.tabsBarShell}
          mt={type !== "theme" ? "lg" : "-sm"}
        >
          <TabScrollIcon scrollRef={scrollRef} dir={"left"} />
          <ScrollArea
            className={className}
            flex={1}
            miw={0}
            pos="relative"
            viewportRef={scrollRef}
            type="never"
            classNames={{
              root: classes.scrollAreaRoot,
              viewport: classes.scrollAreaViewport,
            }}
          >
            <Box
              display="flex"
              className={clsx(classes.tabsContainer, "resource-tabs")}
            >
            {tabs.map((t) => {
              const isHLOnly = t.label.includes("(HL)");
              return (
                <Button
                  key={t.value}
                  component={Link}
                  href={t.value}
                  color={"darkblue"}
                  variant={t.value === activeTabValue ? "filled" : "white"}
                  size="sm"
                  className={t.value === activeTabValue ? "activeTab" : ""}
                >
                  {isHLOnly ? (
                    <>
                      <span className={classes.hlOnlyIndicator}> (HL)</span>
                      {t.label.replace("(HL)", "")}
                    </>
                  ) : (
                    capAll(t.label)
                  )}
                </Button>
              );
            })}
            </Box>
          </ScrollArea>
          <TabScrollIcon scrollRef={scrollRef} dir={"right"} />
        </Box>
      </div>
      <div className={classes.mobileOnly} style={cssVars}>
        <Select
          data={tabs}
          value={activeTabValue}
          comboboxProps={{ position: "bottom" }}
          maxDropdownHeight={400}
          onChange={(value) => router.push(value ?? "")}
          mt="md"
          size="md"
          classNames={{
            input: classes.input,
            dropdown: classes.dropdown,
            option: classes.selectItem,
          }}
        />
      </div>
    </>
  );
}
